import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Percent, TrendingUp, PiggyBank, CreditCard, CheckCircle, 
  Shield, ShieldAlert, Receipt
} from "lucide-react";
import { ArrowRight } from "lucide-react";

const topGuides = [
  {
    title: "Effektiv ränta",
    description: "Den verkliga kostnaden för ditt lån. Lär dig skillnaden mellan nominell och effektiv ränta, och varför effektiv ränta är viktigast när du jämför lån.",
    slug: "effektiv-ranta",
    icon: Percent,
    gradient: "from-cyan-500 to-blue-500",
  },
  {
    title: "Ränta - Rörlig eller bunden?",
    description: "Välja mellan rörlig och bunden ränta. Förstå fördelar, nackdelar och risker med olika räntestrategier för att göra rätt val för din situation.",
    slug: "ranta-rorlig-eller-bunden",
    icon: TrendingUp,
    gradient: "from-indigo-500 to-blue-500",
  },
  {
    title: "Amortera eller spara",
    description: "Strategisk ekonomisk planering. Lär dig när det är smart att amortera snabbare och när du bör prioritera sparande för bättre privatekonomi.",
    slug: "amortera-eller-spara",
    icon: PiggyBank,
    gradient: "from-purple-500 to-pink-500",
  },
  {
    title: "KALP - Kalkylerad årsränta",
    description: "Den riktiga kostnaden för ditt lån med alla avgifter inkluderade. Förstå hur KALP beräknas och varför det är viktigast när du jämför lån.",
    slug: "kalp",
    icon: Receipt,
    gradient: "from-green-500 to-teal-500",
  },
  {
    title: "Kontantinsats och bolånetak",
    description: "85%-regeln förklarad. Allt om hur mycket du måste ha i kontantinsats vid bostadsköp och vad bolånetaket betyder för dig.",
    slug: "kontantinsats-och-bolanetak",
    icon: Percent,
    gradient: "from-blue-500 to-indigo-500",
  },
  {
    title: "Lånelöfte",
    description: "Förhandsbesked från långivare som visar hur mycket du kan låna. Viktigt verktyg vid bostadsköp för att veta din budget innan budgivning.",
    slug: "lanelöfte",
    icon: CheckCircle,
    gradient: "from-purple-500 to-pink-500",
  },
  {
    title: "Kreditupplysning",
    description: "Din kreditvärdighet förklarad. Lär dig vad som påverkar ditt kreditbetyg och hur du kan förbättra dina chanser att få lån med bra villkor.",
    slug: "kreditupplysning",
    icon: Shield,
    gradient: "from-green-500 to-teal-500",
  },
  {
    title: "Lån utan UC 🆕",
    description: "Skydda ditt kreditbetyg vid mindre lån. Förstå fördelar, nackdelar och när det är smart att välja långivare som inte använder UC.",
    slug: "lana-utan-uc",
    icon: ShieldAlert,
    gradient: "from-purple-500 to-indigo-500",
  },
  {
    title: "Ränteavdrag",
    description: "Få tillbaka 30% av räntan. Förstå hur ränteavdrag fungerar, hur mycket du kan få tillbaka och nya regler från 2025.",
    slug: "ranteavdrag",
    icon: Receipt,
    gradient: "from-emerald-500 to-teal-500",
  },
  {
    title: "Reporänta",
    description: "Riksbankens styrränta som påverkar alla lån i Sverige. Förstå hur reporäntan fungerar och hur den påverkar din låneränta.",
    slug: "reporanta",
    icon: TrendingUp,
    gradient: "from-cyan-500 to-blue-500",
  },
];

export const TopGuidesSection = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  return (
    <div className="grid md:grid-cols-2 gap-4 lg:gap-5">
      {topGuides.map((guide) => {
        const Icon = guide.icon;
        return (
          <Card
            key={guide.slug}
            className="group p-4 lg:p-6 cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
            onClick={() => navigate(`/${currentLanguage}/guider/${guide.slug}`)}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 lg:w-14 lg:h-14 rounded-xl bg-gradient-to-br ${guide.gradient} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                <Icon className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
              </div>
              
              <div className="flex-1">
                <h3 className="text-lg lg:text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                  {guide.title}
                </h3>
                
                <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                  {guide.description}
                </p>
                
                <span className="text-primary font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                  Läs mer om {guide.title}
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
};
